import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Upload, X, Check, AlertCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_EXTENSIONS = [".pdf", ".doc", ".docx"];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    organisation: "",
    subject: "",
    message: "",
    consent: false,
    honeypot: "", // Hidden field for spam protection
  });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) {
      newErrors.full_name = "Full name is required";
    }
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }
    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFileError("");

    if (!selectedFile) {
      setFile(null);
      return;
    }

    // Validate file extension
    const ext = "." + selectedFile.name.split(".").pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setFileError(`Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(", ")}`);
      setFile(null);
      return;
    }

    // Validate file size
    if (selectedFile.size > MAX_FILE_SIZE) {
      setFileError("File too large. Maximum size: 5MB");
      setFile(null);
      return;
    }

    setFile(selectedFile);
  };

  const removeFile = () => {
    setFile(null);
    setFileError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot check - if filled, it's likely a bot
    if (formData.honeypot) {
      console.log("Bot detected");
      return;
    }

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append("full_name", formData.full_name);
      submitData.append("email", formData.email);
      submitData.append("organisation", formData.organisation || "");
      submitData.append("subject", formData.subject);
      submitData.append("message", formData.message);
      submitData.append("consent", formData.consent);
      submitData.append("honeypot", formData.honeypot);

      if (file) {
        submitData.append("file", file);
      }

      await axios.post(`${API}/contact`, submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setIsSubmitted(true);
      toast.success("Your message has been sent successfully!");
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage = error.response?.data?.detail || "Failed to send message. Please try again.";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div id="main-content" className="pt-20" data-testid="contact-page">
        <section className="py-16 lg:py-24 bg-white min-h-[80vh] flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-serif text-3xl lg:text-4xl text-slate-900 mb-4">
                Thank You for Your Message
              </h1>
              <p className="text-slate-600 mb-8">
                We have received your enquiry and will respond as soon as possible. You should receive a confirmation email shortly.
              </p>
              <Button
                asChild
                className="bg-slate-900 text-white hover:bg-slate-800"
                data-testid="back-home-btn"
              >
                <Link to="/">Return to Home</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div id="main-content" className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <p className="overline text-brand-primary mb-4">Get in Touch</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-slate-900 leading-tight mb-6">
              Contact the Centre
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed">
              We welcome enquiries from researchers, policymakers, students, media, and anyone interested in our work. Please use the form below to get in touch.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 lg:py-24 bg-slate-50" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <h2 className="font-serif text-2xl text-slate-900 mb-6">
                Contact Information
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-brand-highlight flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-1">Email</h3>
                    <a
                      href="mailto:info@mindcausation.com"
                      className="text-slate-600 hover:text-brand-primary transition-colors"
                      data-testid="contact-email-link"
                    >
                      info@mindcausation.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-brand-highlight flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-brand-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-900 mb-1">Location</h3>
                    <p className="text-slate-600">Australia</p>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <h3 className="font-serif text-lg text-slate-900 mb-4">
                  Response Time
                </h3>
                <p className="text-sm text-slate-600">
                  We aim to respond to all enquiries within 2-3 business days. For urgent matters, please indicate this in the subject line.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="bg-white border border-slate-200">
                <CardContent className="p-6 sm:p-8">
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    {/* Honeypot field - hidden from users */}
                    <div className="hidden" aria-hidden="true">
                      <input
                        type="text"
                        name="honeypot"
                        value={formData.honeypot}
                        onChange={handleInputChange}
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="full_name" className="text-slate-700">
                          Full Name <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="full_name"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleInputChange}
                          className={`mt-1.5 ${errors.full_name ? "border-red-500" : ""}`}
                          placeholder="Your full name"
                          data-testid="input-full-name"
                        />
                        {errors.full_name && (
                          <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> {errors.full_name}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-slate-700">
                          Email Address <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className={`mt-1.5 ${errors.email ? "border-red-500" : ""}`}
                          placeholder="your.email@example.com"
                          data-testid="input-email"
                        />
                        {errors.email && (
                          <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" /> {errors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="organisation" className="text-slate-700">
                        Organisation
                      </Label>
                      <Input
                        id="organisation"
                        name="organisation"
                        value={formData.organisation}
                        onChange={handleInputChange}
                        className="mt-1.5"
                        placeholder="Your organisation (optional)"
                        data-testid="input-organisation"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-slate-700">
                        Subject <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className={`mt-1.5 ${errors.subject ? "border-red-500" : ""}`}
                        placeholder="What is your enquiry about?"
                        data-testid="input-subject"
                      />
                      {errors.subject && (
                        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.subject}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-700">
                        Message <span className="text-red-500">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        className={`mt-1.5 min-h-[150px] ${errors.message ? "border-red-500" : ""}`}
                        placeholder="Please provide details about your enquiry..."
                        data-testid="input-message"
                      />
                      {errors.message && (
                        <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {errors.message}
                        </p>
                      )}
                    </div>

                    {/* File Upload */}
                    <div>
                      <Label className="text-slate-700">
                        Attachment (Optional)
                      </Label>
                      <p className="text-sm text-slate-500 mb-2">
                        Upload a document (PDF, DOC, DOCX). Maximum size: 5MB
                      </p>
                      
                      {file ? (
                        <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          <FileText className="w-8 h-8 text-brand-primary" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">
                              {file.name}
                            </p>
                            <p className="text-xs text-slate-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={removeFile}
                            className="p-1 hover:bg-slate-200 rounded"
                            data-testid="remove-file-btn"
                          >
                            <X className="w-4 h-4 text-slate-500" />
                          </button>
                        </div>
                      ) : (
                        <label
                          htmlFor="file-upload"
                          className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-brand-primary hover:bg-slate-50 transition-colors"
                          data-testid="file-upload-area"
                        >
                          <div className="flex flex-col items-center justify-center pt-5 pb-6">
                            <Upload className="w-8 h-8 text-slate-400 mb-2" />
                            <p className="text-sm text-slate-600">
                              <span className="font-medium text-brand-primary">Click to upload</span> or drag and drop
                            </p>
                            <p className="text-xs text-slate-500">PDF, DOC, DOCX (max 5MB)</p>
                          </div>
                          <input
                            id="file-upload"
                            type="file"
                            className="hidden"
                            accept=".pdf,.doc,.docx"
                            onChange={handleFileChange}
                            data-testid="file-input"
                          />
                        </label>
                      )}
                      {fileError && (
                        <p className="text-sm text-red-500 mt-2 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> {fileError}
                        </p>
                      )}
                    </div>

                    {/* Consent Checkbox */}
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="consent"
                        checked={formData.consent}
                        onCheckedChange={(checked) =>
                          setFormData((prev) => ({ ...prev, consent: checked }))
                        }
                        className="mt-0.5"
                        data-testid="consent-checkbox"
                      />
                      <Label htmlFor="consent" className="text-sm text-slate-600 cursor-pointer">
                        I consent to the Centre for Consciousness Studies Australia storing my submitted information to respond to my enquiry. See our{" "}
                        <Link to="/privacy" className="text-brand-primary hover:underline">
                          Privacy Policy
                        </Link>{" "}
                        for more information.
                      </Label>
                    </div>

                    <Button
                      type="submit"
                      className="w-full sm:w-auto bg-slate-900 text-white hover:bg-slate-800 px-8"
                      disabled={isSubmitting}
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
